import { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const clone = (x) => JSON.parse(JSON.stringify(x));

export const DEFAULT_SERVICES = [
  { slug: "medical-event-cover", icon: "heart", name: "Medical Event Cover",
    tagline: "Tailored medical support for conferences, exhibitions, corporate events and community gatherings of any size.",
    description: "Every event has different medical needs, which is why we offer a range of tailored packages to suit events of all sizes. Our teams range from First Responders to Doctors and Advanced Practitioners.",
    image: "/site-assets/svc-medical.jpg",
    points: ["First responders, nurses, paramedics and doctors", "Medical centres, gazebos and treatment areas", "Fully equipped response vehicles and ambulances", "Event risk assessment and compliance support"] },
  { slug: "sports-event-medical-cover", icon: "activity", name: "Sports Event Medical Cover",
    tagline: "Specialised pitch-side and event-wide medical support for sporting events — from local matches to major competitions.",
    description: "From local grassroots matches to large-scale competitions, our experienced medical teams provide pitch-side and event-wide cover for sporting events of every kind.",
    image: "/site-assets/sports-main.jpg",
    points: ["Pitch-side immediate care teams", "Pre- and post-event medical checks where required", "Ambulance and treatment facilities on site", "Cover for boxing, football, rugby, motorsport and more"] },
];

export const DEFAULT_CALC_TEXTS = {
  hero_title: "Calculate What Medical Staff You Need For Your Event",
  hero_subtitle: "Free Purple/Green Guide Tool + Martyn's Law Compliance",
  hero_image: "/site-assets/hero5.jpg",
  intro_title: "Event medical staffing calculator",
  intro_text: "Built on the Health & Safety Executive's \"Purple Guide\" scoring system, this calculator gives you an evidence-based starting point for your medical provision. Answer the questions below to generate your event risk score and recommended staffing level.",
  disclaimer: "This tool provides a guide only. High-risk or complex events should always be supported by a bespoke, site-specific medical needs assessment.",
  tiers_title: "Staffing by event tiers",
  tiers_text: "Attendance-based guidance for the minimum recommended medical provision at each event tier.",
  table_title: "Risk calculator score conversion table",
  martyns_title: "Overview of Martyn's Law tiers & staff requirements",
};

export const DEFAULT_CONTACT = {
  email: "info@waleseventmedical.co.uk",
  phone: "Available on request",
  location: "Based in Wales, covering the whole UK",
};

export const DEFAULT_BLOG_SETTINGS = {
  enabled: true,
  title: "News & updates",
  subtitle: "Stories, event round-ups and advice from the Wales Event Medical Services team.",
  image: "/site-assets/hero6.jpg",
};

export const DEFAULT_PAGES = { services: true, about: true, calculator: true, contact: true, gallery: true, offers: true, trusted: true };

export const DEFAULT_NAV = [
  { label: "Home", to: "/", page: null, on: true, tid: "site-nav-home" },
  { label: "Services", to: "/services", page: "services", on: true, auto_services: true },
  { label: "Staff Calculator", to: "/staff-calculator", page: "calculator", on: true, tid: "site-nav-calculator" },
  { label: "Blog", to: "/blog", page: "blog", on: true, tid: "site-nav-blog" },
  { label: "Contact Us", to: "/contact-us", page: "contact", on: true, tid: "site-nav-contact" },
];

export const DEFAULT_HEADER = { logo: "", cta_label: "Get a Free Quote", cta_to: "/intake" };

export const DEFAULT_FOOTER = {
  tagline: "Expert medical cover for events across the UK — from first responders to doctors.",
  bottom_note: "CQC-standard clinical governance · DBS-checked medics",
};

export const DEFAULT_PAGE_SECTIONS = {
  home: [
    { id: "hero", type: "hero",
      eyebrow: "UK-Wide Event Medical Cover",
      title: "Expert medical cover for events across the UK",
      subtitle: "From festivals and concerts to sporting events, our professional teams — first responders to doctors — keep your event safe, compliant and in confident hands.",
      images: ["/site-assets/hero1.jpg", "/site-assets/hero2.jpg", "/site-assets/hero3.jpg"],
      stats: [
        { value: "500+", label: "Events Covered" },
        { value: "24/7", label: "Rapid Response" },
        { value: "15+", label: "Years Experience" },
        { value: "100%", label: "CQC-Standard" },
      ],
      primary_btn: { label: "Get a Free Quote", to: "/intake", style: "primary", tid: "site-hero-quote" },
      secondary_btn: { label: "Our Services", to: "/services", style: "light", tid: "site-hero-services" } },
    { id: "trust", type: "trust_bar",
      items: ["CQC-based clinical standards", "Welsh regulations", "DBS-checked medics", "First responders to doctors", "UK-wide coverage"] },
    { id: "who-we-are", type: "text_image", legacy: "who_we_are",
      eyebrow: "Who we are", heading: "Trusted medical cover, delivered with confidence",
      paragraphs: [
        "We are dedicated to providing the highest standard of medical cover, with a strong focus on safeguarding and the wellbeing of every service user. Our team brings extensive experience delivering reliable support at events across the country.",
        "From First Responders to Doctors and Advanced Practitioners, we have the right professionals for every event — ensuring safety, confidence and expert care.",
      ],
      image: "/site-assets/mec-3.jpg", image_side: "right",
      badge: { value: "15+", label: "years keeping events safe" },
      bullets: [],
      buttons: [
        { label: "Get a Quote", to: "/intake", style: "primary", tid: "site-intro-quote" },
        { label: "About Us", to: "/about-us", style: "outline", tid: "site-intro-about" },
      ] },
    { id: "why-choose-us", type: "features", legacy: "why_choose_us",
      eyebrow: "Why choose us", heading: "Everything your event needs to stay safe",
      subtitle: "We combine experienced, fully-vetted medical teams with robust governance so your event is covered from first aid to advanced care.",
      items: [
        { icon: "shield", title: "CQC-Standard Governance", text: "We voluntarily operate to CQC-based clinical governance, infection control and safeguarding standards." },
        { icon: "users", title: "Experienced Medical Teams", text: "From first responders to nurses, paramedics and doctors — the right professionals for every event." },
        { icon: "clock", title: "Rapid On-Site Response", text: "Pitch-side and event-wide cover with fast, expert care when seconds matter most." },
        { icon: "map", title: "Nationwide Coverage", text: "Based in Wales, delivering reliable event medical cover right across the UK." },
      ] },
    { id: "event-cover", type: "checklist", legacy: "event_cover", dark: true,
      eyebrow: "Event cover", heading: "Cover tailored to your event",
      paragraphs: ["Every event has different medical needs, which is why we offer a range of tailored packages to suit events of all sizes. Whether you're hosting a small community gathering or a large public event, we can provide the right level of medical cover to keep everyone safe and supported."],
      items: ["First responders to doctors", "Fully equipped & crewed ambulances", "Event risk assessment support", "Full compliance & governance"],
      image: "/site-assets/event-cover.png",
      buttons: [{ label: "Get a Quote", to: "/intake", style: "primary", tid: "site-cover-quote" }] },
    { id: "calculator-promo", type: "calculator_promo", legacy: "calculator_promo",
      heading: "Not sure how many staff you need?",
      text: "Use our free event medical staffing calculator, based on the HSE Purple Guide risk model, to work out the cover your event requires in minutes.",
      button: { label: "Work out your staffing", to: "/staff-calculator", style: "dark", tid: "site-calculator-cta" },
      points: ["Based on HSE Purple Guide", "Instant staffing estimate", "Event tier guidance"] },
    { id: "reviews", type: "reviews", legacy: "reviews",
      eyebrow: "Reviews", heading: "Trusted by event organisers across the UK",
      subtitle: "Don't just take our word for it — here's what clients say about working with our team.",
      items: [
        { name: "Sarah T.", role: "Festival Organiser", text: "The team were absolutely fantastic — professional, reassuring and prepared for anything. Our attendees were in safe hands all weekend." },
        { name: "Mark D.", role: "Sports Club Chairman", text: "Reliable pitch-side cover every match day. Highly recommend Wales Event Medical Services to any club." },
        { name: "Emma L.", role: "Corporate Events Manager", text: "Booking was easy and the medics were superb. We wouldn't use anyone else for our corporate events." },
      ] },
    { id: "software", type: "cta", style: "plain",
      heading: "Like how we run? This software can run your service too",
      text: "Quotes, invoices, a full CRM, email and CQC-compliant patient reporting — plus a login portal for your own customers. See what it can do, or ask for your login.",
      buttons: [
        { label: "See the software", to: "/software", style: "primary", tid: "site-software-cta" },
        { label: "Ask for your login", to: "/contact-us", style: "outline", tid: "site-login-cta" },
      ] },
    { id: "latest-news", type: "blog_feed",
      eyebrow: "From the blog", heading: "Latest news & updates", subtitle: "", count: 3 },
    { id: "cta", type: "cta", legacy: "cta", style: "banner",
      heading: "Planning an event? Let us keep it safe.",
      text: "Get a fast, no-obligation quote for professional medical cover tailored to your event.",
      buttons: [
        { label: "Get a Free Quote", to: "/intake", style: "white", tid: "site-cta-quote" },
        { label: "Contact Us", to: "/contact-us", style: "light", tid: "site-cta-contact" },
      ] },
  ],
  services: [
    { id: "hero", type: "page_hero", title: "Our Services",
      subtitle: "Professional, tailored medical cover for events of all sizes across the UK.",
      image: "/site-assets/hero4.jpg" },
    { id: "grid", type: "services_grid", variant: "detailed", eyebrow: "", heading: "", subtitle: "" },
    { id: "cta", type: "cta", style: "plain",
      heading: "Not sure what cover you need?",
      text: "Tell us about your event and we'll recommend the right level of medical provision — no obligation.",
      buttons: [
        { label: "Get a Free Quote", to: "/intake", style: "primary", tid: "services-cta-quote" },
        { label: "Try the staff calculator", to: "/staff-calculator", style: "outline", tid: "services-cta-calc" },
      ] },
  ],
  "service-medical": [
    { id: "hero", type: "page_hero", title: "Medical Event Cover",
      subtitle: "Tailored medical support for conferences, exhibitions, corporate events and community gatherings of any size.",
      image: "/site-assets/svc-medical.jpg" },
    { id: "detail", type: "checklist", card: true, eyebrow: "Service", heading: "Medical Event Cover",
      paragraphs: ["Every event has different medical needs, which is why we offer a range of tailored packages to suit events of all sizes. Our teams range from First Responders to Doctors and Advanced Practitioners."],
      list_title: "What's included",
      items: ["First responders, nurses, paramedics and doctors", "Medical centres, gazebos and treatment areas", "Fully equipped response vehicles and ambulances", "Event risk assessment and compliance support", "Festivals, concerts, exhibitions and corporate events", "Community gatherings of any size"],
      buttons: [
        { label: "Get a Quote", to: "/intake", style: "primary", tid: "service-quote" },
        { label: "Staff Calculator", to: "/staff-calculator", style: "outline", tid: "service-calculator" },
      ] },
  ],
  "service-sports": [
    { id: "hero", type: "page_hero", title: "Sports Event Medical Cover",
      subtitle: "Specialised pitch-side and event-wide medical support for sporting events — from local matches to major competitions.",
      image: "/site-assets/sports-main.jpg" },
    { id: "detail", type: "checklist", card: true, eyebrow: "Service", heading: "Sports Event Medical Cover",
      paragraphs: ["From local grassroots matches to large-scale competitions, our experienced medical teams provide pitch-side and event-wide cover for sporting events of every kind."],
      list_title: "What's included",
      items: ["Pitch-side immediate care teams", "Pre- and post-event medical checks where required", "Ambulance and treatment facilities on site", "Boxing, football, rugby, motorsport and more", "Grassroots to major competitions", "Event-wide response cover"],
      buttons: [
        { label: "Get a Quote", to: "/intake", style: "primary", tid: "service-quote" },
        { label: "Staff Calculator", to: "/staff-calculator", style: "outline", tid: "service-calculator" },
      ] },
  ],
  about: [
    { id: "hero", type: "page_hero", title: "About Us",
      subtitle: "Experienced, compliant and ready for any event.",
      image: "/site-assets/hero2.jpg" },
    { id: "offer", type: "chips", eyebrow: "What we offer", heading: "Our services",
      paragraphs: ["We are forever growing our medical team, from First Aiders to Doctors. Our calendar fills up quickly, so please get your quote in as soon as possible. We also have excellent connections within the event industry — if you need any help with the compliance of your event, please get in touch."],
      items: ["Event Cover", "PTS", "Organ & Blood Transport", "Nightclub Medical Staff", "TV & Film Medical Cover", "Vehicle Hire", "Repatriation", "First-Aid Training"] },
    { id: "standards", type: "columns", eyebrow: "Standards & regulation",
      heading: "Our commitment to high standards & regulation",
      paragraphs: ["Although Event Medical Services is now based in Wales — where independent medical providers are not regulated by the Care Quality Commission (CQC) — we continue to work to the same high standards and governance you would expect from a CQC-regulated service in England."],
      note: { title: "We are not CQC-registered — because we're based in Wales.",
        text: "The CQC regulates independent medical providers in England only, so registration doesn't apply to a Wales-based service like ours. Instead, we voluntarily work to CQC-equivalent standards of clinical governance, infection control and safeguarding." },
      tags: ["Safe", "Transparent", "High-quality", "Professionally governed"],
      buttons: [{ label: "Contact Us", to: "/contact-us", style: "dark", tid: "about-contact" }],
      cards: [
        { heading: "What this means for you",
          text: "Even though Welsh legislation does not require CQC registration, we voluntarily follow:",
          items: ["CQC-based policies and clinical governance frameworks", "Strict infection control procedures", "Robust clinical auditing and reporting", "Clear safeguarding protocols for adults and children", "Professional standards for staff training, equipment and recordkeeping"] },
        { heading: "Operating to England's standards — delivered in Wales",
          text: "Our team has already completed the full CQC paperwork, policies and compliance framework, so clients can feel confident that we operate to a regulated standard, even though we are not legally required to register with the CQC in Wales.",
          items: [] },
      ] },
  ],
  contact: [
    { id: "hero", type: "page_hero", title: "Contact Us",
      subtitle: "Get in touch to discuss medical cover for your event.",
      image: "/site-assets/hero6.jpg" },
    { id: "form", type: "contact_block", eyebrow: "Get in touch", heading: "We're here to help",
      paragraphs: ["We provide first aid cover & medical services for a range of events, including festivals, concerts, sporting events and other community events. If you'd like a bespoke quote or to discuss first aid at an event you have planned, please get in touch."],
      form_title: "Send us a message",
      form_note: "Your message comes straight to our inbox — we usually reply within one working day." },
  ],
  offers: [
    { id: "hero", type: "page_hero", title: "What We Can Offer",
      subtitle: "Kit, vehicles and tailored medical cover for events of every size — mix and match whatever your event needs.",
      image: "/site-assets/kit-marquee.jpg" },
    { id: "kit", type: "kit_grid", eyebrow: "Kit & vehicles", heading: "Our equipment", subtitle: "Anything below can be added to your cover — on its own or as part of a package.",
      items: [
        { image: "/site-assets/kit-gazebo.jpg", title: "Pop-up medical gazebo", text: "A sheltered treatment point for first aid and assessments — compact and quick to set up anywhere on site.", more: "marquee" },
        { image: "/site-assets/kit-marquee.jpg", title: "Medical marquee", text: "A full treatment centre for bigger events — multiple treatment bays, privacy and full kit inside.", more: "marquee" },
        { image: "/site-assets/kit-ambulance.jpg", title: "Ambulance on site", text: "A fully equipped ambulance at your event for immediate care — and hospital transfer if it's ever needed.", more: "ambulance" },
        { image: "/site-assets/kit-car.jpg", title: "Rapid response car", text: "Fast, flexible cover for large sites — a medic response car that gets to casualties quickly.", more: "ambulance" },
      ] },
    { id: "detail-marquee", type: "kit_detail", slug: "marquee", eyebrow: "Kit in detail", heading: "Medical marquee",
      photos: ["/site-assets/kit-marquee.jpg", "/site-assets/kit-marquee-inside.jpg"],
      paragraphs: [
        "Our medical marquee is a full treatment centre for bigger events — multiple treatment bays, privacy screens, lighting and full kit inside, staffed by our medics for the duration of your event.",
        "It gives casualties somewhere warm, dry and private to be assessed and treated, and gives your event a visible, professional medical presence.",
      ],
      specs: [{ label: "Sizes", value: "3m × 3m up to 6m × 12m" }, { label: "Set-up time", value: "Around 60–90 minutes" }, { label: "Capacity", value: "Multiple treatment bays" }],
      requests: ["Level ground to pitch on", "Vehicle access close to the pitch site for set-up", "Access to power (or tell us and we'll bring a generator)"] },
    { id: "detail-ambulance", type: "kit_detail", slug: "ambulance", eyebrow: "Kit in detail", heading: "Ambulance on site",
      photos: ["/site-assets/kit-ambulance.jpg", "/site-assets/kit-ambulance-inside.jpg"],
      paragraphs: [
        "A fully equipped ambulance parked at your event — immediate care on scene and, if it's ever needed, a vehicle ready for hospital transfer.",
        "Inside there's a treatment bench, monitoring, oxygen and full medical storage — it's a mobile treatment room, not just transport.",
      ],
      specs: [{ label: "Size", value: "Standard UK ambulance (approx. 6m × 2.5m)" }, { label: "Crew", value: "Two medics as standard" }, { label: "Equipment", value: "Full kit, oxygen, defibrillator & monitoring" }],
      requests: ["A marked parking spot with a clear exit route", "Access from 60 minutes before the event starts", "Power hook-up if we're on site overnight (or tell us and we'll self-power)"] },
    { id: "cta", type: "cta", style: "plain", heading: "Want any of this at your event?",
      text: "Tell us the date and what you're planning — we'll put together a tailored, no-obligation quote.",
      buttons: [{ label: "Get a Free Quote", to: "/intake", style: "primary", tid: "offers-quote-cta" }] },
  ],
  trusted: [
    { id: "hero", type: "page_hero",
      eyebrow: "Recommended partners",
      title: "People we trust",
      subtitle: "Companies we work alongside at events week in, week out — and happily recommend." },
    { id: "partners", type: "partners",
      eyebrow: "Our network", heading: "Trusted event partners",
      subtitle: "Every company below is one we've worked with personally. Mention Wales Event Medical Services when you get in touch.",
      items: [
        { name: "Our security partner", category: "Event security", image: "",
          blurb: "SIA-licensed door supervisors and event security teams.",
          offers: "SIA-licensed door supervisors\nEvent security teams\nCrowd management",
          why: "We've shared dozens of event sites with them — professional, calm and great with the public.",
          review: "", info: "",
          email: "", phone: "", website: "" },
        { name: "Our catering partner", category: "Food & catering", image: "",
          blurb: "Event catering units for the public and crew.",
          offers: "Public catering units\nCrew catering\nLicensed bar options",
          why: "Reliable, well-run setups that keep crowds (and our medics) fed all day.",
          review: "", info: "",
          email: "", phone: "", website: "" },
      ] },
    { id: "cta", type: "cta", style: "banner",
      heading: "Planning an event?",
      text: "We can recommend trusted partners for security, catering and more — just ask.",
      buttons: [{ label: "Get a Free Quote", to: "/intake", style: "primary", tid: "trusted-quote-cta" }] },
  ],
  gallery: [
    { id: "hero", type: "page_hero", title: "Gallery",
      subtitle: "Our teams at events across Wales and the UK.",
      image: "/site-assets/hero4.jpg" },
    { id: "photos", type: "gallery", eyebrow: "", heading: "", subtitle: "",
      images: ["/site-assets/hero1.jpg", "/site-assets/mec-3.jpg", "/site-assets/event-cover.png", "/site-assets/svc-medical.jpg", "/site-assets/sports-main.jpg", "/site-assets/hero2.jpg", "/site-assets/hero5.jpg", "/site-assets/hero6.jpg"] },
    { id: "cta", type: "cta", style: "plain",
      heading: "Want our team at your event?",
      text: "Get a fast, no-obligation quote for professional medical cover tailored to your event.",
      buttons: [{ label: "Get a Free Quote", to: "/intake", style: "primary" }] },
  ],
};

function legacyHome(d) {
  const secs = clone(DEFAULT_PAGE_SECTIONS.home);
  const hero = secs.find((s) => s.type === "hero");
  if (hero) {
    if (d.hero_eyebrow) hero.eyebrow = d.hero_eyebrow;
    if (d.hero_title) hero.title = d.hero_title;
    if (d.hero_subtitle) hero.subtitle = d.hero_subtitle;
    if (Array.isArray(d.hero_images) && d.hero_images.length) hero.images = d.hero_images;
    if (Array.isArray(d.stats) && d.stats.length) hero.stats = d.stats;
  }
  const rev = secs.find((s) => s.type === "reviews");
  if (rev && Array.isArray(d.reviews) && d.reviews.length) rev.items = d.reviews;
  const vis = d.sections || {};
  return secs.filter((s) => !s.legacy || vis[s.legacy] !== false);
}

const DEFAULT_SEO = {
  title: "Event Medical Cover UK | Wales Event Medical Services",
  description: "Professional event medical cover across the UK — first responders to doctors, ambulances and treatment units for festivals, sports, corporate and community events. Free, no-obligation quotes from Wales Event Medical Services.",
  keywords: "event medical cover, event first aid cover, medical cover for events UK, event medics Wales, sports event medical cover, festival medical cover, first responders for events, event ambulance cover",
};

export function normalizeSiteContent(d = {}) {
  const data = d && typeof d === "object" ? d : {};
  const incoming = data.page_sections && typeof data.page_sections === "object" ? data.page_sections : null;
  const page_sections = {};
  for (const key of Object.keys(DEFAULT_PAGE_SECTIONS)) {
    if (incoming && Array.isArray(incoming[key]) && incoming[key].length) {
      page_sections[key] = incoming[key];
    } else if (!incoming && key === "home") {
      page_sections[key] = legacyHome(data);
    } else {
      page_sections[key] = clone(DEFAULT_PAGE_SECTIONS[key]);
    }
  }
  return {
    page_sections,
    services_catalog: Array.isArray(data.services_catalog) && data.services_catalog.length ? data.services_catalog : clone(DEFAULT_SERVICES),
    calculator_texts: { ...DEFAULT_CALC_TEXTS, ...(data.calculator_texts || {}) },
    contact_info: { ...DEFAULT_CONTACT, ...(data.contact_info || {}) },
    blog_settings: { ...DEFAULT_BLOG_SETTINGS, ...(data.blog_settings || {}) },
    pages: { ...DEFAULT_PAGES, ...(data.pages || {}) },
    nav_links: Array.isArray(data.nav_links) && data.nav_links.length ? data.nav_links : clone(DEFAULT_NAV),
    header: { ...DEFAULT_HEADER, ...(data.header || {}) },
    footer: { ...DEFAULT_FOOTER, ...(data.footer || {}) },
    seo: { ...DEFAULT_SEO, ...(data.seo || {}) },
    page_meta: data.page_meta || {},
  };
}

let cache = null;
let inflight = null;
const EMPTY = normalizeSiteContent({});

export function useSiteContent() {
  const [content, setContent] = useState(cache || EMPTY);
  useEffect(() => {
    if (cache) {
      setContent(cache);
      return;
    }
    if (!inflight) {
      inflight = fetch(`${API}/api/site/content`)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
    }
    inflight.then((d) => {
      inflight = null;
      if (d && typeof d === "object" && Object.keys(d).length) {
        cache = normalizeSiteContent(d);
        setContent(cache);
      }
    });
  }, []);
  return content;
}

export function clearSiteContentCache() {
  cache = null;
}

export function usePageMeta(title, description, image, pageKey, robots) {
  const site = useSiteContent();
  const seo = site.seo || {};
  const pm = (pageKey && site.page_meta && site.page_meta[pageKey]) || {};
  const effTitle = pm.title || title || seo.title || "";
  const effDesc = pm.description || description || seo.description || "";
  const keywords = seo.keywords || "";
  useEffect(() => {
    if (effTitle) document.title = effTitle;
    const setMeta = (attr, key, content) => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const origin = window.location.origin;
    setMeta("name", "description", effDesc);
    setMeta("name", "keywords", keywords);
    setMeta("property", "og:title", effTitle);
    setMeta("property", "og:description", effDesc);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", origin + window.location.pathname);
    setMeta("property", "og:image", `${origin}${image || "/site-assets/hero1.jpg"}`);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", effTitle);
    setMeta("name", "twitter:description", effDesc);
    let robotsEl = document.head.querySelector('meta[name="robots"]');
    if (robots) {
      if (!robotsEl) {
        robotsEl = document.createElement("meta");
        robotsEl.setAttribute("name", "robots");
        document.head.appendChild(robotsEl);
      }
      robotsEl.setAttribute("content", robots);
    } else if (robotsEl) {
      robotsEl.remove();
    }
  }, [effTitle, effDesc, keywords, image, pm.title, pm.description, robots]);
}
